import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X, Download, Sparkles, Smartphone } from 'lucide-react';
import { APP_VERSION } from '@/config/version';

// Detect if running as iOS PWA
const isIOSPWA = () => {
  return (
    (window.navigator.standalone === true) || // iOS Safari standalone mode
    (window.matchMedia('(display-mode: standalone)').matches && /iPhone|iPad|iPod/.test(navigator.userAgent))
  );
};

const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

// Compare semantic versions: returns true if v1 > v2
const isNewerVersion = (v1, v2) => {
  if (!v1 || !v2) return false;
  
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;
    
    if (num1 > num2) return true;
    if (num1 < num2) return false;
  }
  
  return false; // versions are equal
};

export function UpdateNotification() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [isIOSApp, setIsIOSApp] = useState(false);

  // Never show in iframe (embedded public pages)
  const isInIframe = window !== window.parent;
  
  const checkApiVersion = useCallback(async () => {
    if (isInIframe) return;
    try {
      // Force fresh fetch, no cache. Use backend URL if configured (dev), else same-origin proxy (prod)
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${apiBase}/api/version?_=` + Date.now(), {
        cache: 'no-store',
        headers: { 
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('[Update] Current:', APP_VERSION, 'Server:', data.version);
        
        // Only show update if server version is NEWER (not just different)
        if (data.version && isNewerVersion(data.version, APP_VERSION)) {
          console.log('[Update] New version available:', data.version);
          setNewVersion(data.version);
          setUpdateAvailable(true);
          localStorage.setItem('available_version', data.version);
          return true;
        } else {
          // Server version is same or older, clear any stored version
          console.log('[Update] Already up to date');
          localStorage.removeItem('available_version');
          setUpdateAvailable(false);
        }
      }
    } catch (error) {
      console.log('[Update] Check failed:', error);
    }
    return false;
  }, []);

  useEffect(() => {
    // Check if iOS PWA
    setIsIOSApp(isIOSPWA());
    
    // Check if we previously detected an update - but only if it's still newer
    const storedVersion = localStorage.getItem('available_version');
    if (storedVersion && isNewerVersion(storedVersion, APP_VERSION)) {
      setNewVersion(storedVersion);
      setUpdateAvailable(true);
    } else {
      // Clear outdated stored version
      localStorage.removeItem('available_version');
    }

    // Listen for service worker messages
    const handleMessage = (event) => {
      if (event.data?.type === 'UPDATE_AVAILABLE' || event.data?.type === 'SW_UPDATED') {
        const version = event.data.newVersion || event.data.version;
        if (version && isNewerVersion(version, APP_VERSION)) {
          console.log('[Update] SW reports new version:', version);
          setNewVersion(version);
          setUpdateAvailable(true);
          localStorage.setItem('available_version', version);
        }
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    // Listen for controller change (most reliable update detection, especially Chrome Desktop PWA)
    const handleControllerChange = () => {
      console.log('[Update] SW controller changed – checking API version');
      checkApiVersion();
    };
    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);

    // Check for waiting service worker
    const checkForWaitingSW = async () => {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration?.waiting) {
          console.log('[Update] Service worker waiting');
          checkApiVersion();
        }
        // Also listen for future SW updates on this registration
        if (registration) {
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'activated') {
                  console.log('[Update] New SW activated via updatefound');
                  checkApiVersion();
                }
              });
            }
          });
        }
      }
    };
    
    checkForWaitingSW();

    // Check API immediately
    checkApiVersion();
    
    // Check again after 2 seconds (for slow connections)
    const quickCheck = setTimeout(() => checkApiVersion(), 2000);
    
    // For iOS PWA: Check on visibility change (when app comes to foreground)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Update] App became visible, checking for updates...');
        checkApiVersion();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Check every 2 minutes
    const interval = setInterval(checkApiVersion, 2 * 60 * 1000);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(quickCheck);
      clearInterval(interval);
    };
  }, [checkApiVersion]);

  const handleUpdate = async () => {
    setUpdating(true);
    
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
        console.log('[Update] All caches cleared');
      }

      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
          console.log('[Update] Service worker unregistered');
        }
      }

      // Clear localStorage update flag
      localStorage.removeItem('available_version');
      localStorage.removeItem('sw_version');

      // For iOS: Need to guide user to close and reopen
      if (isIOS()) {
        // Navigate to /home (not /) to avoid SSR/backend routing issues
        setTimeout(() => {
          window.location.href = window.location.origin + '/home?v=' + Date.now();
        }, 300);
      } else {
        // For other platforms, force reload current page
        setTimeout(() => {
          window.location.href = window.location.origin + '/home?v=' + Date.now();
        }, 300);
      }
    } catch (error) {
      console.error('[Update] Error during update:', error);
      window.location.reload(true);
    }
  };

  // Debug helper
  useEffect(() => {
    window.forceAppUpdate = handleUpdate;
    window.checkAppUpdate = checkApiVersion;
    console.log('[Update] Debug: window.forceAppUpdate() or window.checkAppUpdate()');
  }, [checkApiVersion]);

  if (!updateAvailable || dismissed || isInIframe) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-in">
      <div className="bg-primary text-primary-foreground p-4 shadow-lg border border-primary-foreground/20">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-primary-foreground/20 rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm uppercase tracking-wider">
              Update verfügbar!
            </h3>
            <p className="text-xs opacity-90 mt-1">
              {newVersion ? (
                <>Neue Version <strong>v{newVersion}</strong></>
              ) : (
                <>Eine neue Version ist verfügbar</>
              )}
            </p>
            <p className="text-xs opacity-70 mt-0.5">
              Ihre Version: v{APP_VERSION}
            </p>
            
            {/* iOS-specific instructions */}
            {isIOSApp && (
              <div className="mt-2 p-2 bg-primary-foreground/10 text-xs">
                <div className="flex items-center gap-1.5 font-medium">
                  <Smartphone className="w-3 h-3" />
                  iOS App:
                </div>
                <p className="mt-1 opacity-90">
                  Nach "Aktualisieren" bitte die App komplett schliessen (nach oben wischen) und neu öffnen.
                </p>
              </div>
            )}
            
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary-foreground text-primary font-bold text-xs uppercase tracking-wider hover:bg-primary-foreground/90 disabled:opacity-50 transition-colors"
              >
                {updating ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Aktualisieren...
                  </>
                ) : (
                  <>
                    <Download className="w-3 h-3" />
                    Aktualisieren
                  </>
                )}
              </button>
              
              <button
                onClick={() => setDismissed(true)}
                className="px-3 py-1.5 text-xs uppercase tracking-wider opacity-70 hover:opacity-100 transition-opacity"
              >
                Später
              </button>
            </div>
          </div>
          
          <button
            onClick={() => setDismissed(true)}
            className="flex-shrink-0 p-1 opacity-70 hover:opacity-100 transition-opacity"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
